import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-baseline-ultrasound"),
  kind: "test",
  tags: [
    "clinic-visit",
    "her",
    "pre-cycle",
    "ivf",
    "iui",
    "egg-freezing",
    "afc",
    "testing",
  ],
  title: "Baseline Ultrasound",
  subtitle: "Early-cycle scan to confirm you are ready to start a cycle",
  body: {
    testName: "Baseline Ultrasound",
    category: "monitoring",
    whatItMeasures:
      "Confirms there are no cysts, establishes your antral follicle count (AFC), and checks that the uterine lining is appropriately thin before beginning stimulation or a medicated cycle.",
    howItWorks:
      "On cycle day 2-4, most often day 2 or 3, you come in for a transvaginal ultrasound. The sonographer counts the small resting antral follicles on each ovary. This AFC helps predict how your ovaries may respond to medication. The lining is also measured to ensure it has shed appropriately. If everything looks clear, with no residual cysts and an appropriately thin lining, your clinic will usually clear you to start medications that same day or the next. Call your clinic on cycle day 1 to book the appointment. Continue any pre-cycle medications as directed.",
    quickFacts: [
      { kind: "sample", value: "Transvaginal ultrasound" },
      { kind: "time", value: "15-30 min visit" },
      { kind: "pain", value: "Mild pressure" },
      { kind: "prep", value: "Call on cycle day 1; scan day 2-4" },
      { kind: "results", value: "Usually same day" },
    ],
    costRange: { low: 250, high: 550 },
    atHome: false,
    clinicRequired: true,
    cycleDay: "Day 2-4, most often day 2 or 3 (early follicular phase)",
    markerTiming: [
      {
        marker: "AFC and lining",
        timing: "Cycle day 2-4, most often day 2 or 3",
        reason:
          "The scan is done early because resting follicles are easiest to count before a dominant follicle grows, and the lining should be thin after bleeding.",
      },
    ],
    dataPoints: [
      makeId("dp-follicle-count"),
      makeId("dp-endometrial-thickness"),
    ],
    section: "cycle-monitoring",
  },
  sources: [
    {
      title: "ESHRE guideline: ovarian stimulation for IVF/ICSI",
      authors: "ESHRE Guideline Group on Ovarian Stimulation",
      journal: "Human Reproduction Open",
      year: 2020,
      url: "https://academic.oup.com/hropen/article/2020/2/hoaa009/5826933",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Testing and interpreting measures of ovarian reserve: a committee opinion",
      authors:
        "Practice Committee of the American Society for Reproductive Medicine",
      journal: "Fertility and Sterility",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/testing-and-interpreting-measures-of-ovarian-reserve-a-committee-opinion-2020/",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 3,
};

registerBlock(test);
